


import { useState, useEffect, useCallback, memo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { IRouteName, injectRouter } from '../../interface/route';
import Controller from '../../controllers/languageselect';
import { mapRouteToProps, equals as ArgsEquals, mapPropsToUserState } from './arguments';
import type { IRouteLanguageSelectArguments } from '../../controllers/languageselect/interface';
import { injectQueryURL } from '../../interface/util';
import { injectConfigAPI } from '../../interface/config';
import { injectUserSession, IStateUserSession } from '../../interface/state';
import useUserSession from '../../components/common/useUserSession';


const updateStateLanguageId = (languageId: number): Promise<void> => {
    return new Promise( async function(resolve, reject)  {
        try {
            const userState = injectUserSession();
            const newUserState: IStateUserSession = {...(await userState.current() ?? userState.new()), languageId, };
            await userState.save(newUserState);
            resolve()
        } catch (e) {
            reject(e)
        }
    })
}

function LanguageSelectRoute(props: RouteComponentProps<{}>) : JSX.Element {
    const query = injectQueryURL();
    const router = injectRouter();
    const configAPI = injectConfigAPI();
    const { userState: { state } } = useUserSession()
    const userStateInject = injectUserSession();

    const [route, setRoute] = useState<IRouteLanguageSelectArguments>(mapRouteToProps(props, query, state ?? userStateInject.new(), configAPI));

    const didRequestTourSelect = useCallback( async (id: number) => {
        await updateStateLanguageId(id);
        router.push(IRouteName.TourSelect, props.history, {
            ...route,
            languageId: id,
        });
    }, [route, props, router]);

    const didRequestBack = useCallback(() => {
        updateStateLanguageId(-1);

        if ( !router.pop(props.history, route) ) {
            /* if we fail to go back, reset the app & go to Launch route */
            router.push(IRouteName.Launch, props.history, route, {replace: true});
        }
    }, [route, props, router]);

    useEffect(() => {
        const newRoute = mapRouteToProps(props, query, state ?? userStateInject.new(), configAPI);
        if ( ArgsEquals(newRoute, route) ) { return; }
        setRoute(newRoute);
    }, [props]);

    useEffect(() => {
        const newState = mapPropsToUserState(route, state ?? userStateInject.new())
        userStateInject.save(newState);
    }, [route])

    return (
        <Controller.renderer
            key="LanguageSelectRoute"
            didRequestTourSelect={didRequestTourSelect}
            didRequestBack={didRequestBack}
            route={route}
        />
    )
}

const propsEqual = (prev: RouteComponentProps<{}>, next: RouteComponentProps<{}>) => {
    return (
        ( prev.location.search === next.location.search ) &&
        ( prev.location.pathname === next.location.pathname )
    );
}

export default memo(LanguageSelectRoute, propsEqual);

